<template>
  <div class="image_cropper">
    <div class="cropper_content">
      <div class="cropper_wrapper">
        <img ref="cropperImage" :src="imageUrl" @load="initCropper" />
        <div class="cropper_overlay">
          <div class="cropper_selection" @mousedown="handleSelectionMouseDown" @touchstart="handleSelectionTouchStart">
            <!-- Resize handles -->
            <div class="resize-handle top-left" @mousedown="handleResizeMouseDown($event, 'top-left')"></div>
            <div class="resize-handle top-right" @mousedown="handleResizeMouseDown($event, 'top-right')"></div>
            <div class="resize-handle bottom-left" @mousedown="handleResizeMouseDown($event, 'bottom-left')"></div>
            <div class="resize-handle bottom-right" @mousedown="handleResizeMouseDown($event, 'bottom-right')"></div>
          </div>
        </div>
      </div>
    </div>
    <div class="cropper_footer">
      <button class="cancel_btn" @click="handleCancel">Cancel</button>
      <button class="confirm_btn" @click="handleConfirm" :disabled="isProcessing">
        <span v-if="isProcessing">Processing...</span>
        <span v-else>Confirm</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted } from 'vue';

import { compressImage } from '@/utils/imageCompressor';

const props = defineProps({
  imageFile: {
    type: File,
    required: true,
  },
});

const emit = defineEmits(['confirm', 'cancel']);

const cropperImage = ref(null);
const imageUrl = ref('');
let cropSize = 200; // 最大剪裁大小为 200*200
const isProcessing = ref(false);
let startX = 0;
let startY = 0;
let isDragging = false;
let isResizing = false;
let resizeDirection = '';
let startCropSize = 0;
let cropperWrapper = null;
let cropperOverlay = null;
let cropperSelection = null;
let selectionPosition = { x: 0, y: 0 }; // 剪裁区域位置

onMounted(() => {
  // Create object URL for the image
  imageUrl.value = URL.createObjectURL(props.imageFile);

  // Get references to elements
  cropperWrapper = document.querySelector('.cropper_wrapper');
  cropperOverlay = document.querySelector('.cropper_overlay');

  // Add event listeners for dragging and resizing
  if (cropperWrapper) {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Touch events for mobile
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
  }
});

onUnmounted(() => {
  // Clean up event listeners
  if (cropperWrapper) {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);

    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleTouchEnd);
  }

  // Revoke object URL
  if (imageUrl.value) {
    URL.revokeObjectURL(imageUrl.value);
  }
});

const initCropper = () => {
  // Initialize cropper position
  updateCropper();

  // Set image to center position
  if (cropperImage.value) {
    // Reset image position to center
    cropperImage.value.style.left = '50%';
    cropperImage.value.style.top = '50%';
    cropperImage.value.style.transform = 'translate(-50%, -50%)';
  }

  // Get cropper selection reference
  cropperSelection = document.querySelector('.cropper_selection');

  // Set initial selection position and size
  if (cropperSelection) {
    selectionPosition = { x: 0, y: 0 };
    updateSelectionSize();
    updateSelectionPosition();
  }
};

const updateCropper = () => {
  if (cropperOverlay) {
    // Center the cropper
    cropperOverlay.style.display = 'flex';
    cropperOverlay.style.justifyContent = 'center';
    cropperOverlay.style.alignItems = 'center';
  }
};

const updateSelectionPosition = () => {
  if (cropperSelection) {
    // Update selection position
    cropperSelection.style.transform = `translate(${selectionPosition.x}px, ${selectionPosition.y}px)`;
  }
};

const updateSelectionSize = () => {
  if (cropperSelection) {
    // Update selection size
    cropperSelection.style.width = `${cropSize}px`;
    cropperSelection.style.height = `${cropSize}px`;
  }
};

const handleSelectionMouseDown = (e) => {
  e.stopPropagation();
  isDragging = true;
  startX = e.clientX;
  startY = e.clientY;
  document.body.style.cursor = 'grabbing';
};

const handleResizeMouseDown = (e, direction) => {
  e.stopPropagation();
  isResizing = true;
  resizeDirection = direction;
  startX = e.clientX;
  startY = e.clientY;
  startCropSize = cropSize;
  document.body.style.cursor = 'nesw-resize';
};

const handleSelectionTouchStart = (e) => {
  e.stopPropagation();
  if (e.touches.length > 0) {
    isDragging = true;
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
  }
};

const handleMouseMove = (e) => {
  if (isDragging) {
    const deltaX = e.clientX - startX;
    const deltaY = e.clientY - startY;

    // Calculate new position
    const newX = selectionPosition.x + deltaX;
    const newY = selectionPosition.y + deltaY;

    // Get image and selection dimensions
    const imgRect = cropperImage.value.getBoundingClientRect();
    const selectionHalfWidth = cropSize / 2;
    const selectionHalfHeight = cropSize / 2;

    // Calculate boundaries
    const minX = -imgRect.width / 2 + selectionHalfWidth;
    const maxX = imgRect.width / 2 - selectionHalfWidth;
    const minY = -imgRect.height / 2 + selectionHalfHeight;
    const maxY = imgRect.height / 2 - selectionHalfHeight;

    // Clamp position within boundaries
    const clampedX = Math.max(minX, Math.min(maxX, newX));
    const clampedY = Math.max(minY, Math.min(maxY, newY));

    // Update selection position
    selectionPosition.x = clampedX;
    selectionPosition.y = clampedY;
    updateSelectionPosition();

    startX = e.clientX;
    startY = e.clientY;
  } else if (isResizing) {
    const deltaX = e.clientX - startX;
    const deltaY = e.clientY - startY;

    // Calculate the distance from the center of the selection
    const selectionRect = cropperSelection.getBoundingClientRect();
    const centerX = selectionRect.left + selectionRect.width / 2;
    const centerY = selectionRect.top + selectionRect.height / 2;

    // Calculate the distance from the center to the start point
    const startDistance = Math.sqrt((startX - centerX) * (startX - centerX) + (startY - centerY) * (startY - centerY));
    // Calculate the distance from the center to the current point
    const currentDistance = Math.sqrt(
      (e.clientX - centerX) * (e.clientX - centerX) + (e.clientY - centerY) * (e.clientY - centerY)
    );

    // Calculate the difference in distance
    const distanceDelta = currentDistance - startDistance;

    // Calculate new crop size (maintain aspect ratio)
    let newCropSize = startCropSize + distanceDelta;

    // Limit crop size between 50 and 200
    newCropSize = Math.max(50, Math.min(200, newCropSize));

    // Update crop size
    cropSize = newCropSize;
    updateSelectionSize();
  }
};

const handleMouseUp = () => {
  isDragging = false;
  isResizing = false;
  resizeDirection = '';
  document.body.style.cursor = 'default';
};

const handleTouchMove = (e) => {
  if (!isDragging || e.touches.length === 0) return;

  const deltaX = e.touches[0].clientX - startX;
  const deltaY = e.touches[0].clientY - startY;

  // Update selection position
  selectionPosition.x += deltaX;
  selectionPosition.y += deltaY;
  updateSelectionPosition();

  startX = e.touches[0].clientX;
  startY = e.touches[0].clientY;
};

const handleTouchEnd = () => {
  isDragging = false;
};

const handleCancel = () => {
  emit('cancel');
};

const handleConfirm = async () => {
  isProcessing.value = true;

  try {
    // Get cropped image
    const croppedBlob = await cropImage();

    // Create a canvas for resizing to 200*200
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');

    // Load the cropped image and draw it to the canvas
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, 200, 200);

      // Convert canvas to blob
      canvas.toBlob(
        async (finalBlob) => {
          // Create a new File from the final blob
          const finalFile = new File([finalBlob], 'compressed_avatar.jpg', {
            type: 'image/jpeg',
          });

          emit('confirm', finalFile);
          isProcessing.value = false;
        },
        'image/jpeg',
        0.8
      );
    };
    img.src = URL.createObjectURL(croppedBlob);
  } catch (error) {
    console.error('Error processing image:', error);
    emit('cancel');
    isProcessing.value = false;
  }
};

const cropImage = () => {
  return new Promise((resolve, reject) => {
    const img = cropperImage.value;
    if (!img) {
      reject(new Error('Image not loaded'));
      return;
    }

    // Create canvas for cropping
    const canvas = document.createElement('canvas');
    canvas.width = cropSize;
    canvas.height = cropSize;
    const ctx = canvas.getContext('2d');

    // Calculate crop position
    const imgRect = img.getBoundingClientRect();
    const wrapperRect = cropperWrapper.getBoundingClientRect();

    // Calculate the actual image size and position
    const imgWidth = img.naturalWidth;
    const imgHeight = img.naturalHeight;

    // Calculate the scale factor based on the displayed size
    // 由于图片可能被缩放以适应容器，我们需要计算实际的缩放因子
    const scaleX = imgRect.width / imgWidth;
    const scaleY = imgRect.height / imgHeight;
    // 使用平均缩放因子，确保宽度和高度的缩放一致
    const scale = (scaleX + scaleY) / 2;

    // Calculate the image's top-left corner position
    const imgLeft = imgRect.left;
    const imgTop = imgRect.top;

    // Calculate the cropper's top-left corner position
    const cropperLeft = wrapperRect.left + (wrapperRect.width - cropSize) / 2 + selectionPosition.x;
    const cropperTop = wrapperRect.top + (wrapperRect.height - cropSize) / 2 + selectionPosition.y;

    // Calculate the offset between the image and cropper
    const offsetX = (cropperLeft - imgLeft) / scale;
    const offsetY = (cropperTop - imgTop) / scale;

    // Calculate the crop coordinates in the original image
    const cropX = offsetX;
    const cropY = offsetY;

    // Calculate the actual crop size in original image pixels
    const actualCropSize = cropSize / scale;

    // Draw cropped image
    ctx.drawImage(img, cropX, cropY, actualCropSize, actualCropSize, 0, 0, cropSize, cropSize);

    // Convert canvas to blob
    canvas.toBlob(resolve, 'image/jpeg', 0.8);
  });
};
</script>

<style lang="less" scoped>
.image_cropper {
  width: 100%;
  background: #fff;
  border-radius: 20px;
  overflow: hidden;

  .cropper_content {
    padding: 20px;

    .cropper_wrapper {
      position: relative;
      width: 100%;
      height: 300px;
      overflow: hidden;
      border: 1px solid #f0f0f0;
      border-radius: 8px;
      cursor: grab;

      &:active {
        cursor: grabbing;
      }

      img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        z-index: 1;
      }

      .cropper_overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 2;
        pointer-events: none;

        .cropper_selection {
          width: 200px;
          height: 200px;
          background: transparent;
          border: 4px solid #fff;
          border-radius: 4px;
          box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.5);
          cursor: grab;
          position: relative;
          z-index: 3;
          overflow: visible;
          pointer-events: auto;

          &:active {
            cursor: grabbing;
          }

          .resize-handle {
            position: absolute;
            width: 12px;
            height: 12px;
            background: #fff;
            border: 2px solid #05644f;
            border-radius: 50%;
            cursor: nesw-resize;
            z-index: 4;
          }

          .top-left {
            top: -6px;
            left: -6px;
          }

          .top-right {
            top: -6px;
            right: -6px;
          }

          .bottom-left {
            bottom: -6px;
            left: -6px;
          }

          .bottom-right {
            bottom: -6px;
            right: -6px;
          }
        }
      }
    }
  }

  .cropper_footer {
    display: flex;
    gap: 16px;
    justify-content: flex-end;
    padding: 20px;

    button {
      padding: 10px 20px;
      border-radius: 100px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s;

      &.cancel_btn {
        background: #f5f5f5;
        color: #333;
        border: 1px solid #e0e0e0;

        &:hover {
          background: #e0e0e0;
        }
      }

      &.confirm_btn {
        background: #05644f;
        color: #fff;
        border: none;

        &:hover:not(:disabled) {
          background: #045a45;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }
    }
  }
}
</style>
